import { defineStore, acceptHMRUpdate } from 'pinia';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import { fetchMinimalUsers } from '@/stores/users.utils';

// Corresponds to UserMinimizedPublic in pendo-appengine/server/model/user.go
export interface UserMinimal {
    id: string;
    username: string;
    first: string;
    last: string;
}

interface UsersMinimalState {
    error: unknown;
    fetching: boolean;
    map: Record<string, UserMinimal>;
}

export function getInitialState (): UsersMinimalState {
    return {
        error: null,
        fetching: false,
        map: {},
    };
}

export const useUsersMinimalStore = defineStore('usersMinimal', {
    state: getInitialState,
    getters: {
        list: (state): UserMinimal[] => values(state.map),
        userById: (state) => (id: string): UserMinimal | undefined => state.map[id],
        userByUsername: (state) => (username: string): UserMinimal | undefined => {
            return values(state.map).find(
                (user) => user.username.toLowerCase().trim() === username.toLowerCase().trim(),
            );
        },
    },
    actions: {
        setMap (map: Record<string, UserMinimal>) {
            this.map = map;
        },
        setMapAtKey (user: UserMinimal) {
            this.map[user.id] = user;
        },
        setError (error: unknown) {
            this.error = error;
        },
        setFetching (fetching: boolean) {
            this.fetching = fetching;
        },
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (!isEmpty(this.map) && !reFetch) return;
            this.setFetching(true);
            try {
                const userList = await fetchMinimalUsers();
                this.setMap(keyBy(userList, 'id') as Record<string, UserMinimal>);
            } catch (error) {
                this.setError(error);
            }
            this.setFetching(false);
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useUsersMinimalStore, module.hot));
}
